import React from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { SCREEN_MAX_WIDTH } from '../constants'
import { useFooterSlot } from './ScreenShell'

/* The CTA bar at the bottom of a screen.

   It is declared deep inside each screen (often inside <form>, sometimes inside
   ScreenContainer) but must render *outside* the shell's scroll region, so it
   portals into a slot ScreenShell owns. Keeping it in the React tree where it is
   declared means form context, submit handlers and validation state all keep
   working unchanged; only its DOM position moves.

   It is no longer `position: fixed`. See ScreenShell for why (WEB-895). */
const Bar = styled.div<{ $expanded?: boolean }>`
  padding: var(--grid-16) var(--grid-24) var(--grid-24);
  background: white;
  display: flex;
  justify-content: center;
  position: relative;

  /* Fades the content scrolling up behind the bar's top edge. Sits above the
     bar rather than inside it, and never takes pointer events. */
  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 100%;
    height: ${({ $expanded }) => ($expanded ? '48px' : '16px')};
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, white 100%);
    pointer-events: none;
  }
`

export const StickyFooterContent = styled.div`
  width: 100%;
  max-width: ${SCREEN_MAX_WIDTH}px;
`

type Props = {
  children?: React.ReactNode
  $expanded?: boolean
  className?: string
}

export const StickyFooter: React.FC<Props> = ({ children, $expanded, className }) => {
  const footerSlot = useFooterSlot()
  const bar = (
    <Bar $expanded={$expanded} className={className} data-testid="screen-footer">
      {children}
    </Bar>
  )

  /* No shell (unit tests rendering a screen in isolation): render in place. */
  if (footerSlot === undefined) return bar
  /* Slot attaches in the shell's first commit; wait for it rather than render
     inside the scroller and remount on the move. */
  if (footerSlot === null) return null

  return createPortal(bar, footerSlot)
}
